<template>
  <div>
    <b-container fluid>
      <b-card no-body>
        <b-overlay
            :show="isLoading"
            rounded="sm"
        >
          <div class="m-2">
            <b-row v-if="isSystemAdmin() || creatable">
              <b-col cols="6">
                <h3>
                  Các quyền cho vai trò <strong><u>{{ roleName }}</u></strong>
                </h3>
              </b-col>
              <b-col cols="6">
                <div class="text-right">
                  <b-button
                      v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                      variant="warning"
                      class="mr-1"
                      @click="onRefresh"
                  >
                    <span class="text-nowrap text-right">
                      <feather-icon icon="RefreshCwIcon" /> Làm mới
                    </span>
                  </b-button>
                  <b-button
                      v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                      variant="primary"
                      @click="onSaveChanges"
                  >
                    <span class="text-nowrap text-right">
                      <feather-icon icon="SaveIcon" /> Lưu thay đổi
                    </span>
                  </b-button>
                </div>
              </b-col>
            </b-row>
            <b-row :class="{'mt-2': isSystemAdmin() || creatable}">
              <b-col cols="12">
                <vue-good-table
                    mode="remote"
                    :columns="columns"
                    :rows="permissionMatrix"
                    :pagination-options="paginationOptions"
                    :total-rows="totalRows"
                    :line-numbers="true"
                    @on-page-change="onPageChange"
                    @on-sort-change="onSortChange"
                    @on-column-filter="onColumnFilter"
                    @on-per-page-change="onPerPageChange"
                >
                  <div
                      slot="emptystate"
                      style="text-align: center; font-weight: bold"
                  >
                    Không có bản ghi nào !
                  </div>
                  <template
                      slot="table-row"
                      slot-scope="props"
                  >
                    <span v-if="props.column.field === 'resourceCode'">
                      {{ props.row.resourceCode }}
                    </span>
                    <span v-else-if="props.column.field === 'resourceName'">
                      {{ props.row.resourceName }}
                    </span>
                    <span v-else-if="props.column.field === 'all'">
                      <b-form-checkbox
                          v-model="props.row.allActive"
                          @change="(checked) => handleAllPermissionsChange(props.row, checked)"
                      />
                    </span>
                    <span v-else>
                      <b-form-checkbox
                          v-model="props.row.permissions[props.column.field].active"
                          @change="(checked) => handlePermissionChange(props.row, props.column.field, checked)"
                      />
                    </span>
                  </template>

                  <!-- pagination -->
                  <template
                      slot="pagination-bottom"
                      slot-scope="props"
                  >
                    <div class="d-flex justify-content-between flex-wrap">
                      <div class="d-flex align-items-center mb-0 mt-1">
                        <span class="text-nowrap"> Hiển thị 1 đến </span>
                        <b-form-select
                            v-model="filter.itemsPerPage"
                            :options="itemsPerPageOptions"
                            class="mx-1"
                            @input="(value) => props.perPageChanged({ currentPerPage: value })"
                        />
                        <span class="text-nowrap">của {{ permissionMatrix.length }} bản ghi</span>
                      </div>
                      <div>
                        <b-pagination
                            :value="1"
                            :total-rows="totalRows"
                            :per-page="filter.itemsPerPage"
                            class="mt-1 mb-0"
                            @input="(value) => props.pageChanged({ currentPage: value })"
                        />
                      </div>
                    </div>
                  </template>
                </vue-good-table>
              </b-col>
            </b-row>
          </div>
        </b-overlay>
      </b-card>
    </b-container>
  </div>
</template>

<script>
import Ripple from 'vue-ripple-directive'
import {
  BButton, BCard, BCol, BContainer, BFormSelect, BOverlay, BPagination, BRow, BFormCheckbox,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import '@core/scss/vue/libs/vue-good-table.scss'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { hasPermissionForResource, isSystemAdmin } from '@/utils'
import { PermissionCode, ResourceCode } from '@/const/code'
import apiMain from '@/views/authorization/role-permission-resource/api'

export default {
  name: 'RolePermissionResource',
  directives: {
    Ripple,
  },
  components: {
    BButton,
    BCard,
    BCol,
    BContainer,
    BFormSelect,
    BOverlay,
    BPagination,
    BRow,
    BFormCheckbox,
    VueGoodTable,
  },
  data() {
    return {
      isLoading: false,
      filter: {
        currentPage: 1,
        itemsPerPage: 1000000,
        resourceName: null,
        resourceCode: null,
        sort: null,
      },
      currentResource: undefined,
      paginationOptions: {
        enabled: true,
      },
      itemsPerPageOptions: [10, 20, 30, 50, 80, 100],
      selectedId: +this.$route.params.id,
      roleName: '',
      permissionTypes: [],
      changedPermissions: new Map(), // Store changed permissions for batch update
      matrixOrigin: [], // Store original matrix state
      totalRows: 0,
      permissionResources: [],
    }
  },
  computed: {
    columns() {
      return [
        {
          label: 'Mã đối tượng',
          field: 'resourceCode',
          filterOptions: {
            enabled: true,
            trigger: 'enter',
            placeholder: 'Nhập mã đối tượng',
            filterValue: this.filter.resourceCode,
          },
          thClass: 'text-center',
        },
        {
          label: 'Đối tượng',
          field: 'resourceName',
          filterOptions: {
            enabled: true,
            trigger: 'enter',
            placeholder: 'Nhập đối tượng',
            filterValue: this.filter.resourceName,
          },
          thClass: 'text-center',
        },
        {
          label: 'ALL',
          field: 'all',
          sortable: false,
          thClass: 'text-center',
          tdClass: 'text-center',
        },
        ...this.permissionTypes.map(type => ({
          label: type,
          field: type,
          sortable: false,
          thClass: 'text-center',
          tdClass: 'text-center',
        })),
      ]
    },
    permissionMatrix() {
      // Transform permissionResources into matrix format
      const matrix = []
      const resourceMap = new Map()

      // Return empty array if no permission resources
      if (!this.permissionResources || this.permissionResources.length === 0) {
        return []
      }

      // Get unique permission types from API response
      const uniquePermissions = [...new Set(this.permissionResources.map(pr => pr.permissionCode))]
      this.permissionTypes = uniquePermissions

      this.permissionResources.forEach(pr => {
        if (!resourceMap.has(pr.resourceCode)) {
          resourceMap.set(pr.resourceCode, {
            resourceCode: pr.resourceCode,
            resourceName: pr.resourceName,
            permissions: {},
          })
        }
        const resource = resourceMap.get(pr.resourceCode)
        resource.permissions[pr.permissionCode] = {
          active: pr.active === 1,
          originalActive: pr.active === 1, // Store original state for comparison
        }
      })

      // Calculate initial allActive state
      const result = Array.from(resourceMap.values())
      result.forEach(row => {
        row.allActive = this.isAllPermissionsActive(row)
      })

      // Store original state
      this.matrixOrigin = JSON.parse(JSON.stringify(this.permissionResources))

      return result
    },
    creatable() {
      return isSystemAdmin() || hasPermissionForResource(PermissionCode.CREATE, ResourceCode.ROLE_PERMISSION_RESOURCE)
    },
    deletable() {
      return isSystemAdmin() || hasPermissionForResource(PermissionCode.DELETE, ResourceCode.ROLE_PERMISSION_RESOURCE)
    },
  },
  async created() {
    this.isLoading = true
    try {
      const response = await apiMain.getPermissionResources({
        id: this.selectedId,
        params: this.filter,
      })
      if (response) {
        this.permissionResources = response.records
        this.totalRows = response.totalRows
      }
      if (this.permissionResources.length > 0) {
        this.roleName = this.permissionResources[0].roleName
      }
    } catch (e) {
      this.showToast(`[${e.code}] ${e.message}`, 'XCircleIcon', 'danger')
    } finally {
      this.isLoading = false
    }
  },
  methods: {
    isSystemAdmin,
    showToast(title, icon, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon,
          variant,
        },
      })
    },
    handlePermissionChange(row, permissionType, checked) {
      const key = `${row.resourceCode}_${permissionType}`
      const originalState = row.permissions[permissionType].originalActive

      if (checked === originalState) {
        // If changed back to original state, remove from changed list
        this.changedPermissions.delete(key)
      } else {
        // Store the change
        this.changedPermissions.set(key, {
          resourceCode: row.resourceCode,
          permissionCode: permissionType,
          active: checked,
        })
      }

      // Update allActive state based on all permissions
      row.allActive = this.isAllPermissionsActive(row)
    },
    async onSaveChanges() {
      if (this.changedPermissions.size === 0) {
        this.showToast('Không có thay đổi nào để lưu', 'InfoIcon', 'info')
        return
      }

      this.isLoading = true
      try {
        const changes = Array.from(this.changedPermissions.values())
        const response = await apiMain.updateRolePermissionResources({
          id: this.selectedId,
          params: changes
        })
        if (response) {
          const { isSuccessful, message } = response
          if (isSuccessful) {
            this.showToast('Cập nhật quyền thành công', 'CheckIcon', 'success')
            try {
              const response = await apiMain.getPermissionResources({
                id: this.selectedId,
                params: this.filter,
              })
              if (response) {
                this.permissionResources = response.records
                this.totalRows = response.totalRows
              }
            } catch (e) {
              this.showToast(`[${e.code}] ${e.message}`, 'XCircleIcon', 'danger')
            }
          } else {
            this.showToast(message, 'XCircleIcon', 'danger')
          }
        }
        this.changedPermissions.clear()
      } catch (e) {
        this.showToast(`[${e.code}] ${e.message}`, 'XCircleIcon', 'danger')
      } finally {
        this.isLoading = false
      }
    },
    updateParams(newProps) {
      this.filter = { ...this.filter, ...newProps }
    },
    async onPageChange(param) {
      this.updateParams({ currentPage: param.currentPage })
      await this.getPermissionResourcesFromStore()
    },
    async getPermissionResourcesFromStore() {
      this.isLoading = true
      try {
        const response = await apiMain.getPermissionResources({
          id: this.selectedId,
          params: this.filter,
        })
        if (response) {
          this.permissionResources = response.records
          this.totalRows = response.totalRows
        }
      } catch (e) {
        this.showToast(`[${e.code}] ${e.message}`, 'XCircleIcon', 'danger')
      } finally {
        this.isLoading = false
      }
    },
    async onPerPageChange(param) {
      this.updateParams({ currentPage: 1, itemsPerPage: param.currentPerPage })
      await this.getPermissionResourcesFromStore()
    },
    async onSortChange(param) {
      if (param.length > 0) {
        const { field, type } = param[0]
        switch (field) {
          case 'resourceCode':
            this.updateParams({ sort: `1_${type}` })
            break
          case 'resourceName':
            this.updateParams({ sort: `2_${type}` })
            break
          default:
            this.updateParams({ sort: `3_${type}` })
            break
        }
        await this.getPermissionResourcesFromStore()
      }
    },
    async onColumnFilter(param) {
      const { columnFilters } = param
      let shouldUpdate = false

      if (Object.hasOwn(columnFilters, 'resourceName') && columnFilters.resourceName !== this.filter.resourceName) {
        this.updateParams({ resourceName: columnFilters.resourceName })
        shouldUpdate = true
      }
      if (Object.hasOwn(columnFilters, 'resourceCode') && columnFilters.resourceCode !== this.filter.resourceCode) {
        this.updateParams({ resourceCode: columnFilters.resourceCode })
        shouldUpdate = true
      }

      if (shouldUpdate) {
        await this.getPermissionResourcesFromStore()
      }
    },
    async onSucceed() {
      await this.getPermissionResourcesFromStore()
    },
    isAllPermissionsActive(row) {
      return this.permissionTypes.every(type => row.permissions[type]?.active)
    },
    handleAllPermissionsChange(row, checked) {
      // Update all permissions
      this.permissionTypes.forEach(type => {
        if (row.permissions[type]) {
          row.permissions[type].active = checked
          // Call handlePermissionChange to track changes
          this.handlePermissionChange(row, type, checked)
        }
      })
      // Update allActive state
      row.allActive = checked
    },
    async onRefresh() {
      this.isLoading = true
      try {
        // Reset all changes
        this.changedPermissions.clear()
        this.permissionResources = this.matrixOrigin
        this.showToast('Đã làm mới dữ liệu', 'CheckIcon', 'success')
      } catch (e) {
        this.showToast(`[${e.code}] ${e.message}`, 'XCircleIcon', 'danger')
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>
